import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { company, navLinks } from "../mock/mock";
import { Button } from "./ui/button";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
      window.scrollTo({ top: 0 });
    }
  };

  const dark = scrolled || open || location.pathname !== "/";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        dark ? "bg-[#FBF8F3]/95 backdrop-blur-md shadow-sm py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <button onClick={() => handleNav("/#home")} className="flex items-center gap-2 group">
          <span
            className={`font-serif text-2xl md:text-[28px] tracking-tight transition-colors ${
              dark ? "text-[#2A2521]" : "text-white"
            }`}
          >
            Go<span className="text-[#A17E4A]">Drapery</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <button
              key={l.label}
              onClick={() => handleNav(l.href)}
              className={`text-sm tracking-wide relative after:absolute after:left-0 after:-bottom-1 after:h-px after:bg-[#A17E4A] after:transition-all after:duration-300 after:w-0 hover:after:w-full ${
                dark ? "text-[#2A2521] hover:text-[#A17E4A]" : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={company.phoneHref}
            className={`flex items-center gap-2 text-sm ${dark ? "text-[#2A2521]" : "text-white"}`}
          >
            <Phone className="w-4 h-4 text-[#A17E4A]" />
            {company.phone}
          </a>
          <Button
            onClick={() => handleNav("/#contact")}
            className="bg-[#A17E4A] hover:bg-[#8a6a3c] text-white rounded-full px-6"
          >
            Free Consultation
          </Button>
        </div>

        <button
          className={`lg:hidden ${dark ? "text-[#2A2521]" : "text-white"}`}
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FBF8F3] border-t border-[#E7E0D6] mt-3 px-5 py-4 flex flex-col gap-1">
          {navLinks.map((l) => (
            <button
              key={l.label}
              onClick={() => handleNav(l.href)}
              className="text-left py-3 text-[#2A2521] border-b border-[#EFE9DF] last:border-0"
            >
              {l.label}
            </button>
          ))}
          <a href={company.phoneHref} className="flex items-center gap-2 py-3 text-[#A17E4A]">
            <Phone className="w-4 h-4" /> {company.phone}
          </a>
        </div>
      )}
    </header>
  );
}
